/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AlertCircle,
  Archive,
  CheckCircle2,
  Clock,
  ExternalLink,
  Link2,
  UserPlus,
} from 'lucide-react';
import { useWorkspacePresence } from '../../hooks/useWorkspacePresence';
import { isThreadAnalyzed, threadDisplayTitle } from '../../lib/clinic-logic';
import { mailThreadLink } from '../../lib/mail-links';
import { getPriorityMeta } from '../../lib/task-list';
import { getActiveWorkspaceId, trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import type { EmailThreadMetadata, LabelCategoryMapping, TriageCategory } from '../../types/email';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { UserAvatar } from '../../ui/user-avatar';
import { AnalysisNotice } from './AnalysisNotice';
import { LabelBadgeList } from './LabelBadgeList';

interface TriageThreadCardProps {
  thread: EmailThreadMetadata;
  labelMap?: Record<string, string>;
  mappings?: LabelCategoryMapping[];
  categories?: TriageCategory[];
  workspaceMembers?: Array<{ id: number; email: string; fullName: string | null }>;
  isMobile?: boolean;
  onArchive?: (threadId: string) => void;
  onDelegate?: (threadId: string) => void;
  onCreateTask?: (threadId: string) => void;
  onTaskClick?: (taskId: number) => void;
  onCardClick?: (threadId: string) => void;
  /** Re-run analysis for one thread; shown only when the failure is retryable. */
  onRetryAnalysis?: (threadId: string) => void;
  isReadOnly?: boolean;
}

export function TriageThreadCard({
  thread,
  labelMap = {},
  mappings = [],
  categories = [],
  workspaceMembers = [],
  isMobile = false,
  onArchive,
  onDelegate,
  onCreateTask,
  onTaskClick,
  onCardClick,
  onRetryAnalysis,
  isReadOnly = false,
}: TriageThreadCardProps) {
  const { url: mailUrl, label: mailLinkLabel, providerName } = mailThreadLink(thread);
  const task = thread.task;

  const meQuery = trpc.auth.me.useQuery();
  const presence = useWorkspacePresence(getActiveWorkspaceId(), meQuery.data?.uid ?? null);
  const viewers = (presence.usersByContext[thread.threadId] || []).filter(
    (id) => id !== meQuery.data?.uid
  );

  return (
    <article
      className={cn(
        'group flex flex-col border-border border-b transition-colors',
        isMobile ? 'gap-0 px-3 py-3' : 'py-5'
      )}
    >
      <div className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 flex-1">
            <div
              onClick={(e) => {
                if (onCardClick) {
                  e.preventDefault();
                  onCardClick(thread.threadId);
                }
              }}
              className="flex items-center gap-2 group/title cursor-pointer"
            >
              <h3
                className={cn(
                  'font-display font-normal leading-tight transition-colors group-hover/title:text-primary',
                  isMobile ? 'text-sm' : 'text-xl'
                )}
              >
                {threadDisplayTitle(thread)}
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {thread.contact && (
                <Badge
                  variant="outline"
                  className={cn(
                    'bg-muted border-border text-muted-foreground gap-1 px-1.5 py-0.5 font-medium shrink-0 ',
                    isMobile ? 'text-[8px] h-4' : 'text-[10px]'
                  )}
                >
                  <Link2 className="h-2.5 w-2.5 shrink-0" />
                  {thread.contact.label}
                </Badge>
              )}
              <LabelBadgeList
                thread={thread}
                labels={thread.labels || []}
                labelMap={labelMap}
                categories={categories}
                mappings={mappings}
                maxVisible={isMobile ? 3 : 10}
                showCategoryDots={!isMobile}
                badgeClassName={cn(
                  'bg-muted border-none px-2 py-0.5 gap-1',
                  isMobile ? 'text-[9px] h-4' : 'text-[10px]'
                )}
                renderLabel={(l) =>
                  l === 'YELLOW_STAR'
                    ? '⭐'
                    : labelMap[`${thread.accountEmail}::${l}`] || labelMap[l] || l
                }
              />
              {/* The AI-derived badges are shown only when there is an analysis
 behind them. Rendered unconditionally they degrade into an empty
 category chip and an empty difficulty chip styled green — which
 reads as "Easy", a claim the model never made. */}
              {isThreadAnalyzed(thread) ? (
                <>
                  {!isMobile && (
                    <Badge
                      variant="outline"
                      className="bg-muted border-none px-2 py-0.5 text-[10px]"
                    >
                      {thread.domainCategory}
                    </Badge>
                  )}
                  <Badge
                    variant="secondary"
                    className={cn(
                      'px-2 py-0.5 border-none',
                      isMobile ? 'text-[9px] h-4' : 'text-[10px]',
                      thread.difficultyLevel === 'Hard'
                        ? 'bg-muted text-foreground'
                        : thread.difficultyLevel === 'Moderate'
                          ? 'bg-muted text-foreground'
                          : 'bg-muted text-foreground'
                    )}
                  >
                    {thread.difficultyLevel}
                  </Badge>
                  {thread.estimatedEffortMinutes !== null && (
                    <div
                      className={cn(
                        'flex items-center gap-1 text-muted-foreground ml-1',
                        isMobile ? 'text-[9px]' : 'text-[10px]'
                      )}
                    >
                      <Clock className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />{' '}
                      {thread.estimatedEffortMinutes}m
                    </div>
                  )}
                </>
              ) : (
                <AnalysisNotice thread={thread} onRetry={onRetryAnalysis} />
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {viewers.length > 0 && (
              <div className="flex -space-x-1.5" title="Currently viewing this thread">
                {viewers.map((userId) => (
                  <UserAvatar
                    key={userId}
                    user={workspaceMembers.find((m) => m.id === userId)}
                    size="xs"
                    className="border-2 border-background"
                  />
                ))}
              </div>
            )}
            {thread.resolvedAt && (
              <Badge
                variant="outline"
                className={cn(
                  'bg-muted text-foreground border-border gap-1',
                  isMobile ? 'text-[9px] px-1.5 h-4' : 'text-[10px]'
                )}
              >
                <CheckCircle2 className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                Resolved
              </Badge>
            )}
            {thread.threadState && (
              <Badge
                variant="outline"
                className={cn(
                  'bg-primary/5 text-primary border-primary/20',
                  isMobile ? 'text-[9px] px-1.5 h-4' : 'text-[10px]'
                )}
              >
                {thread.threadState}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className={cn('space-y-4', isMobile ? 'pb-1' : '')}>
        {/* Omitted entirely without an analysis, rather than rendered as an empty
 "Core Ask" panel. The badge row above already says why. */}
        {thread.coreAsk && (
          <div
            className={cn(
              'bg-primary/5 border border-primary/10 rounded-lg',
              isMobile ? 'p-3' : 'p-4'
            )}
          >
            <div className="flex gap-3">
              <AlertCircle
                className={cn('text-primary shrink-0 mt-0.5', isMobile ? 'h-4 w-4' : 'h-5 w-5')}
              />
              <div className="space-y-1">
                {!isMobile && <p className="text-xs font-semibold text-primary">Core Ask</p>}
                <p
                  className={cn(
                    'font-medium leading-relaxed',
                    isMobile ? 'text-xs line-clamp-2' : 'text-sm'
                  )}
                >
                  {thread.coreAsk}
                </p>
              </div>
            </div>
          </div>
        )}

        {!isMobile && thread.extractedEntities && (
          <div className="flex flex-wrap gap-4">
            {thread.extractedEntities.keyStakeholders?.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] font-medium text-muted-foreground">Stakeholders</p>
                <div className="flex -space-x-2">
                  {thread.extractedEntities.keyStakeholders.map((s: string, i: number) => (
                    <div
                      key={i}
                      className="h-6 w-6 rounded-full bg-muted border-2 border-background flex items-center justify-center text-[10px] font-semibold"
                      title={s}
                    >
                      {s[0]}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {thread.extractedEntities.deadlines?.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] font-medium text-muted-foreground">Deadlines</p>
                <div className="text-xs font-semibold text-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {thread.extractedEntities.deadlines.join(', ')}
                </div>
              </div>
            )}
          </div>
        )}

        <div
          className={cn(
            'flex items-center justify-between pt-2 border-t border-border/50',
            isMobile ? 'gap-2' : ''
          )}
        >
          <div className="flex gap-2">
            {onArchive && !isReadOnly && (
              <Button
                variant="ghost"
                size={isMobile ? 'icon' : 'sm'}
                className={cn(
                  isMobile
                    ? 'h-7 w-7 text-muted-foreground hover:text-foreground'
                    : 'h-8 text-xs gap-1.5 hover:bg-muted'
                )}
                onClick={() => onArchive(thread.threadId)}
                title="Archive Thread"
              >
                <Archive className={isMobile ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5'} />
                {!isMobile && 'Archive'}
              </Button>
            )}
            <Button
              variant="ghost"
              size={isMobile ? 'icon' : 'sm'}
              className={cn(
                'gap-1.5',
                isMobile
                  ? 'h-7 w-7 text-muted-foreground hover:bg-muted'
                  : 'h-8 text-xs hover:bg-muted text-muted-foreground'
              )}
              asChild
            >
              <a href={mailUrl} target="_blank" rel="noopener noreferrer" title={mailLinkLabel}>
                <ExternalLink className={isMobile ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5'} />
                {!isMobile && providerName}
              </a>
            </Button>
          </div>
          <div className="flex gap-2">
            {onDelegate && !isReadOnly && (
              <Button
                variant={isMobile ? 'ghost' : 'outline'}
                size={isMobile ? 'icon' : 'sm'}
                className={cn(
                  isMobile
                    ? 'h-7 w-7 text-muted-foreground hover:text-foreground'
                    : 'h-8 text-xs gap-1.5 border-primary/20 hover:bg-primary/5'
                )}
                onClick={() => onDelegate(thread.threadId)}
                title="Delegate Thread"
              >
                <UserPlus className={isMobile ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5'} />
                {!isMobile && 'Delegate'}
              </Button>
            )}
            {onCreateTask && (
              <Button
                variant={isMobile ? 'ghost' : thread.task ? 'outline' : 'default'}
                size={isMobile ? 'icon' : 'sm'}
                className={cn(
                  isMobile
                    ? thread.task
                      ? 'h-7 w-7 text-foreground hover:bg-muted'
                      : 'h-7 w-7 text-primary hover:bg-primary/5'
                    : 'h-8 text-xs gap-1.5 ',
                  thread.task && !isMobile
                    ? 'border-border text-foreground bg-muted hover:bg-muted'
                    : ''
                )}
                onClick={() => !thread.task && onCreateTask(thread.threadId)}
                title={
                  thread.task ? 'Prescribed' : isReadOnly ? 'Read-only workspace' : 'Prescribe'
                }
                disabled={!!thread.task || isReadOnly}
              >
                <CheckCircle2 className={cn('h-3.5 w-3.5', thread.task ? 'text-foreground' : '')} />
                {!isMobile && (task ? 'Prescribed' : 'Prescribe')}
              </Button>
            )}
          </div>
        </div>

        {task && (
          <div
            onClick={() => onTaskClick?.(task.id)}
            className={cn(
              'mt-4 pt-3 border-t border-dashed border-border bg-muted flex flex-col gap-2 transition-all',
              onTaskClick ? 'hover:bg-muted cursor-pointer' : '',
              isMobile ? '-mx-3 px-3 -mb-3 pb-3' : '-mx-6 px-6 -mb-6 pb-6'
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-foreground shrink-0">
                  <CheckCircle2 className="h-3 w-3" />
                </div>
                <span className="text-xs font-semibold text-foreground line-clamp-1">
                  Task: {task.label}
                </span>
              </div>
              <Badge
                variant="outline"
                className="bg-muted border-border text-foreground text-[9px] font-semibold shrink-0"
              >
                {task.status}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
              <span>
                Assignee:{' '}
                <span className="font-medium text-foreground ">
                  {task.assigneeName || task.assigneeEmail || 'Me'}
                </span>
              </span>
              {task.priority !== undefined && (
                <span>
                  Priority:{' '}
                  <span className="font-medium text-foreground ">
                    {getPriorityMeta(task.priority).label}
                  </span>
                </span>
              )}
              {task.dueAt && (
                <span>
                  Due:{' '}
                  <span className="font-medium text-foreground">
                    {new Date(task.dueAt * 1000).toLocaleDateString()}
                  </span>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
